import { paginationParams } from "@/utils/pagination";
import request from "@/utils/request";

/* Shared */
export async function getPricingSettings(referenceId) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/pricing-setting/${referenceId}`, {
    method: "GET",
  });

  return result?.data;
}

export async function setPricingSettings(
  strategyId,
  concurrencyStamp,
  procedure,
  shipperStrategy,
  transporterStrategy,
) {
  const data = {
    shipperStrategy,
    transporterStrategy,
    concurrencyStamp,
    procedure,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/pricing-setting/${strategyId}`, {
    method: "PUT",
    data,
  });

  return result?.data;
}

/* Dealer & Variable */
export async function variableDealerGetStrategy(referenceId) {
  const result = await request(
    `${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/variable/${referenceId}`,
    {
      method: "GET",
    },
  );

  return result?.data;
}

export async function variableDealerCreate(referenceId, margin, minimum) {
  const payload = {
    referenceId,
    margin,
    minimum,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/variable`, {
    method: "POST",
    data: payload,
  });

  return result?.data;
}

export async function variableDealerUpdate(concurrencyStamp, margin, minimum, strategyId) {
  const data = {
    concurrencyStamp,
    margin,
    minimum,
  };

  const result = await request(
    `${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/variable/${strategyId}`,
    {
      method: "PUT",
      data,
    },
  );

  return result?.data;
}

/* Dealer & Residential */
export async function residentialDealerGetStrategy(referenceId) {
  const result = await request(
    `${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/residential/${referenceId}`,
    {
      method: "GET",
    },
  );

  return result?.data;
}

export async function residentialDealerCreate(referenceId, margin) {
  const payload = {
    referenceId,
    margin,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/residential`, {
    method: "POST",
    data: payload,
  });

  return result?.data;
}

export async function residentialDealerUpdate(concurrencyStamp, margin, strategyId) {
  const data = {
    concurrencyStamp,
    margin,
  };

  const result = await request(
    `${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/residential/${strategyId}`,
    {
      method: "PUT",
      data,
    },
  );

  return result?.data;
}

/* Dealer & Bracketed Mileage */
export async function bracketedMileageDealerGetStrategy(referenceId, page = 1, pageSize = 10) {
  const { SkipCount, MaxResultCount } = paginationParams(page, pageSize);

  const params = {
    referenceId,
    SkipCount,
    MaxResultCount,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/bracketed-mileage`, {
    method: "GET",
    params,
  });

  return result?.data;
}

export async function bracketedMileageDealerAddRow(referenceId, amount, distance) {
  const payload = {
    referenceId,
    amount,
    distance,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/bracketed-mileage`, {
    method: "POST",
    data: payload,
  });

  return result?.data;
}

export async function bracketedMileageDealerUpdateRow(rowId, referenceId, amount, distance) {
  const payload = {
    referenceId,
    amount,
    distance,
  };

  const result = await request(
    `${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/bracketed-mileage/${rowId}`,
    {
      method: "PUT",
      data: payload,
    },
  );

  return result?.data;
}

export async function bracketedMileageDealerDeleteRow(rowId) {
  const result = await request(
    `${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/bracketed-mileage/${rowId}`,
    {
      method: "DELETE",
    },
  );

  return result?.data;
}

/* Dealer & Pricing Bands */
export async function pricingBandsDealerGetStrategy(referenceId, page = 1, pageSize = 10) {
  const { SkipCount, MaxResultCount } = paginationParams(page, pageSize);

  const params = new URLSearchParams({
    referenceId,
    SkipCount,
    MaxResultCount,
    Sorting: "distancelower asc",
  }).toString();

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/bands?${params}`, {
    method: "GET",
  });

  return result?.data;
}

export async function pricingBandsDealerGetSettings(referenceId) {
  const result = await request(
    `${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/band-settings/${referenceId}`,
    {
      method: "GET",
    },
  );

  return result?.data;
}

export async function pricingBandsDealerSetSettings(id, concurrencyStamp, threshold, flatRate, pricePerMile) {
  const data = {
    concurrencyStamp,
    threshold,
    flatRate,
    pricePerMile,
  };

  const result = await request(
    `${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/band-settings/${id}`,
    {
      method: "PUT",
      data,
    },
  );

  return result?.data;
}

export async function pricingBandsDealerCreateRow(referenceId, distanceLower, distanceUpper, price) {
  const data = {
    referenceId,
    distanceLower,
    distanceUpper,
    price,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/bands`, {
    method: "POST",
    data,
  });

  return result?.data;
}

export async function pricingBandsDealerUpdateRow(
  id,
  concurrencyStamp,
  referenceId,
  distanceLower,
  distanceUpper,
  price,
) {
  const data = {
    referenceId,
    price,
    distanceLower,
    distanceUpper,
    concurrencyStamp,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/bands/${id}`, {
    method: "PUT",
    data,
  });

  return result?.data;
}

export async function pricingBandsDealerDeleteRow(rowId) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/bands/${rowId}`, {
    method: "DELETE",
  });

  return result?.data;
}

/* Dealer & Fixed by Zip */
export async function fixedByZipDealerGetStrategy(referenceId, page = 1, pageSize = 10) {
  const { SkipCount, MaxResultCount } = paginationParams(page, pageSize);

  const params = {
    referenceId,
    SkipCount,
    MaxResultCount,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/fixed`, {
    method: "GET",
    params,
  });

  return result?.data;
}

export async function fixedByZipDealerCreate(
  referenceId,
  zipFrom,
  zipTo,
  priceType,
  direction,
  vehicleBreakpointPrices,
) {
  const data = {
    referenceId,
    originZipCode: zipFrom,
    destinationZipCode: zipTo,
    vehicleBreakpointPrices,
    priceType,
    direction,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/fixed`, {
    method: "POST",
    data,
  });

  return result?.data;
}

export async function fixedByZipDealerUpdateRow(
  zipFrom,
  zipTo,
  priceType,
  id,
  concurrencyStamp,
  vehicleBreakpointPrices,
) {
  const data = {
    originZipCode: zipFrom,
    destinationZipCode: zipTo,
    vehicleBreakpointPrices,
    priceType,
    concurrencyStamp,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/fixed/${id}`, {
    method: "PUT",
    data,
  });

  return result?.data;
}

export async function fixedByZipDealerDeleteRow(rowId) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/fixed/${rowId}`, {
    method: "DELETE",
  });

  return result?.data;
}

/* Dealer & Cost Plus */
export async function costPlusDealerGetStrategy(referenceId) {
  const result = await request(
    `${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/cost-plus/${referenceId}`,
    {
      method: "GET",
    },
  );

  return result?.data;
}

export async function costPlusDealerCreate(referenceId, amount) {
  const data = {
    referenceId,
    amount,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/cost-plus`, {
    method: "POST",
    data,
  });

  return result?.data;
}

export async function costPlusDealerUpdate(concurrencyStamp, amount, strategyId) {
  const data = {
    concurrencyStamp,
    amount,
  };

  const result = await request(
    `${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/shipper-pricing/cost-plus/${strategyId}`,
    {
      method: "PUT",
      data,
    },
  );

  return result?.data;
}

/* Transporter & Fixed by Zip */
export async function fixedByZipTransporterGetStrategy(referenceId, page = 1, pageSize = 10) {
  const { SkipCount, MaxResultCount } = paginationParams(page, pageSize);

  const params = new URLSearchParams({
    referenceId,
    SkipCount,
    MaxResultCount,
  }).toString();

  const result = await request(
    `${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/transporter-pricing/fixed?${params}`,
    {
      method: "GET",
    },
  );

  return result?.data;
}

export async function fixedByZipTransporterAddRow(referenceId, zipFrom, zipTo, price, direction) {
  const data = {
    referenceId,
    originZipCode: zipFrom,
    destinationZipCode: zipTo,
    price,
    direction,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/transporter-pricing/fixed`, {
    method: "POST",
    data,
  });

  return result?.data;
}

export async function fixedByZipTransporterUpdateRow(zipFrom, zipTo, price, id, concurrencyStamp) {
  const data = {
    originZipCode: zipFrom,
    destinationZipCode: zipTo,
    price,
    concurrencyStamp,
  };

  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/transporter-pricing/fixed/${id}`, {
    method: "PUT",
    data,
  });

  return result?.data;
}

export async function fixedByZipTransporterDeleteRow(rowId) {
  const result = await request(`${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/transporter-pricing/fixed/${rowId}`, {
    method: "DELETE",
  });

  return result?.data;
}

export async function dealerCanAutoAccept(dealerId) {
  const result = await request(
    `${REACT_APP_API_BASE_URL_NEW}/api/pricing-service/pricing-setting/can-auto-accept/${dealerId}`,
    {
      method: "GET",
    },
  );

  if ("error" in result) {
    throw new Error(result.error);
  }

  return result?.data;
}
